import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GameShell, shuffle } from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';
import { formatGameCopy } from './gameCopy';

const { normalizeBrainGameLevel } = brainGameLevels;

const point = () => ({
    x: 9 + Math.random() * 82,
    y: 9 + Math.random() * 82,
});

const createOrbitRound = (level = 1) => {
    const difficultyIndex = level - 1;
    const count = Math.min(9, 6 + Math.floor(difficultyIndex / 3));
    const targetCount = difficultyIndex >= 6 ? 3 : 2;
    const dots = Array.from({ length: count }, (_, index) => ({
        id: `orbit-${index}`,
        path: [point(), point(), point(), point()],
    }));

    return {
        dots,
        id: `${Date.now()}-${Math.random()}`,
        targetIds: shuffle(dots).slice(0, targetCount).map((dot) => dot.id),
        trackMs: Math.max(2800, 4200 - difficultyIndex * 90),
    };
};

function OrbitTrackerGame({ copy = {}, initialLevel, ...props }) {
    const startingLevel = normalizeBrainGameLevel('orbit-tracker', initialLevel);
    const timersRef = useRef([]);
    const [round, setRound] = useState(() => createOrbitRound(startingLevel));
    const [phase, setPhase] = useState('mark');
    const [selected, setSelected] = useState([]);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [tone, setTone] = useState('ready');

    const clearTimers = useCallback(() => {
        timersRef.current.forEach((timer) => window.clearTimeout(timer));
        timersRef.current = [];
    }, []);

    const beginRound = useCallback((nextStreak = 0) => {
        clearTimers();
        const nextLevel = normalizeBrainGameLevel('orbit-tracker', startingLevel + nextStreak);
        const nextRound = createOrbitRound(nextLevel);
        setRound(nextRound);
        setSelected([]);
        setTone('ready');
        setPhase('mark');

        timersRef.current.push(window.setTimeout(() => {
            setPhase('track');
            timersRef.current.push(window.setTimeout(() => setPhase('answer'), nextRound.trackMs));
        }, 1100));
    }, [clearTimers]);

    useEffect(() => {
        beginRound(0);
        return clearTimers;
    }, [beginRound, clearTimers]);

    const chooseDot = (dotId) => {
        if (phase !== 'answer' || selected.includes(dotId)) {
            return;
        }

        if (!round.targetIds.includes(dotId)) {
            setSelected((current) => [...current, dotId]);
            setStreak(0);
            setTone('miss');
            setPhase('feedback');
            timersRef.current.push(window.setTimeout(() => beginRound(0), 900));
            return;
        }

        const nextSelected = [...selected, dotId];
        setSelected(nextSelected);
        setTone('good');

        if (nextSelected.length === round.targetIds.length) {
            const nextStreak = streak + 1;
            setScore((current) => current + 1);
            setStreak(nextStreak);
            setPhase('feedback');
            timersRef.current.push(window.setTimeout(() => beginRound(nextStreak), 700));
        }
    };

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level: normalizeBrainGameLevel('orbit-tracker', startingLevel + streak), score, streak }}
            tone={tone}
        >
            <div className="UFOV-brain-games__game-cue">
                {phase === 'mark' || phase === 'track' ? copy.orbitTrackCue : copy.orbitTapCue}
            </div>
            <div className={`UFOV-brain-games__orbit is-${phase}`} aria-label={copy.orbitTrackCue}>
                {round.dots.map((dot, index) => {
                    const target = round.targetIds.includes(dot.id);
                    const picked = selected.includes(dot.id);
                    const revealTarget = phase === 'mark' || phase === 'feedback';
                    const [start, second, third, end] = dot.path;

                    return (
                        <button
                            aria-label={formatGameCopy(copy.answerOption, { number: index + 1 })}
                            className={`${revealTarget && target ? 'is-target' : ''} ${picked ? (target ? 'is-correct' : 'is-wrong') : ''}`.trim()}
                            disabled={phase !== 'answer'}
                            key={`${round.id}-${dot.id}`}
                            onClick={() => chooseDot(dot.id)}
                            style={{
                                '--orbit-duration': `${round.trackMs}ms`,
                                '--x0': `${start.x}%`, '--y0': `${start.y}%`,
                                '--x1': `${second.x}%`, '--y1': `${second.y}%`,
                                '--x2': `${third.x}%`, '--y2': `${third.y}%`,
                                '--x3': `${end.x}%`, '--y3': `${end.y}%`,
                            }}
                            type="button"
                        />
                    );
                })}
            </div>
        </GameShell>
    );
}

export default OrbitTrackerGame;
